import React, { useMemo, useState } from "react";

import { skipToken, useQuery } from "@tanstack/react-query";

import { getFieldChangeTimeline } from "services";

import { useSearchParams } from "utils";

import { DEFAULT_CACHE_TIME, GET_FIELD_CHANGE_TIMELINE } from "consts";

import { ChangeEventTimelineScope, ChangeEventTimelineView } from "models";

import { AdminHead, AlertBanner, Button, InputSearch, MainPage, SelectSegmented, SkeletonWrapper, Typography, Message as message } from "components";

import { ChangeEventCard } from "pages/admin/verifications/components/changeEvents/ChangeEventCard";
import { ChangeEventFilters } from "pages/admin/verifications/components/changeEvents/ChangeEventFilters";
import { ChangeEventHeader } from "pages/admin/verifications/components/changeEvents/ChangeEventHeader";
import {
  FAMILIES,
  SCOPES,
  countsFor,
  dayOf,
  extractUuid,
  isDefaultView,
  isUuid,
  matchesFilters,
  yearOptions,
} from "pages/admin/verifications/utils/changeEventTimeline";

const FILTER_PARAMS = ["scope", "fam", "q", "sort", "baseline", "from", "to"];

const listParam = <T extends string>(params: URLSearchParams, key: string, all: T[]): T[] => {
  const raw = params.get(key);
  if (!raw) return all.slice();

  const wanted = raw.split(",");
  const picked = all.filter((value) => wanted.indexOf(value) !== -1);
  return picked.length ? picked : all.slice();
};

/** The view lives in the URL so a narrowed timeline can be shared or reloaded — never in localStorage, where a hidden filter would survive silently. */
const viewFromParams = (params: URLSearchParams): ChangeEventTimelineView => ({
  scope: listParam(params, "scope", SCOPES),
  fam: listParam(params, "fam", FAMILIES),
  q: params.get("q") ?? "",
  sort: params.get("sort") === "asc" ? "asc" : "desc",
  baseline: params.get("baseline") !== "0",
  from: params.get("from") ?? "",
  to: params.get("to") ?? "",
});

export const FieldChangeEvents = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState(searchParams.get("fieldId") ?? "");

  const fieldId = searchParams.get("fieldId") ?? "";
  const year = Number(searchParams.get("year")) || null;
  const view = useMemo(() => viewFromParams(searchParams), [searchParams]);

  const { data, error, isFetching } = useQuery({
    queryKey: [GET_FIELD_CHANGE_TIMELINE, fieldId, year],
    queryFn: fieldId && isUuid(fieldId) ? () => getFieldChangeTimeline({ fieldId: fieldId, year: year }) : skipToken,
    gcTime: DEFAULT_CACHE_TIME,
    staleTime: DEFAULT_CACHE_TIME,
  });

  const counts = useMemo(() => countsFor(data?.events ?? []), [data]);
  const matched = useMemo(() => (data?.events ?? []).filter((event) => matchesFilters(event, view)), [data, view]);
  const ordered = useMemo(() => (view.sort === "desc" ? matched.slice().reverse() : matched), [matched, view.sort]);

  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.keys(patch).forEach((key) => {
      const value = patch[key];
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace: true });
  };

  const patchView = (patch: Partial<ChangeEventTimelineView>) =>
    patchParams({
      ...("scope" in patch ? { scope: patch.scope?.length === SCOPES.length ? null : (patch.scope ?? []).join(",") } : {}),
      ...("fam" in patch ? { fam: patch.fam?.length === FAMILIES.length ? null : (patch.fam ?? []).join(",") } : {}),
      ...("q" in patch ? { q: patch.q ?? null } : {}),
      ...("sort" in patch ? { sort: patch.sort === "desc" ? null : "asc" } : {}),
      ...("baseline" in patch ? { baseline: patch.baseline ? null : "0" } : {}),
      ...("from" in patch ? { from: patch.from ?? null } : {}),
      ...("to" in patch ? { to: patch.to ?? null } : {}),
    });

  const resetView = () => patchParams(FILTER_PARAMS.reduce((acc, key) => ({ ...acc, [key]: null }), {}));

  const load = () => {
    const found = extractUuid(draft);
    if (!found) {
      message({ content: "That is not a field uuid. Paste the id itself, or any text containing it.", icon: "gremlin" });
      return;
    }

    patchParams({ fieldId: found, year: null });
  };

  /** A filtered-out cause has no card to scroll to, so say so instead of scrolling nowhere. */
  const jumpTo = (eventId: string) => {
    const target = document.getElementById(`ev-${eventId}`);
    if (!target) {
      message({ content: "That event is hidden by the current filters.", icon: "gremlin" });
      return;
    }

    target.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  const hiddenScopes = SCOPES.filter((scope) => view.scope.indexOf(scope) === -1);
  const hiddenCount = hiddenScopes.reduce((sum, scope: ChangeEventTimelineScope) => sum + counts.byScope[scope], 0);

  return (
    <MainPage pageName="admin">
      <div className="ce-page">
        <AdminHead
          headline="Field change events"
          description="Every change_events row that concerns one field — what changed, who changed it, what it caused."
        />

        <div className="ce-row ce-load">
          <InputSearch
            className="ce-search"
            value={draft}
            label="fieldId"
            placeholder="field uuid, or any text containing one"
            isSmallVariant
            onChange={(inputEvent) => setDraft(inputEvent.target.value)}
            onPressEnter={load}
          />
          <Button className="ce-load-button" type="primary" size="small" loading={isFetching} onClick={load}>
            Load
          </Button>
          {!!data && (
            <>
              <span className="ce-label">statement year</span>
              <SelectSegmented
                options={yearOptions(data).map((option) => ({ label: String(option), value: option }))}
                value={data.year}
                onChange={(next: number) => patchParams({ year: String(next) })}
              />
              <span className="ce-hint">points, payout and balances only</span>
            </>
          )}
        </div>

        {!!error && (
          <AlertBanner
            type="error"
            headingSize="paragraph-s"
            descriptionSize="paragraph-s"
            iconName="tabler/alert-triangle"
            iconSize={20}
            title="The timeline could not be loaded"
            description="A 404 means no field row and no history for that id — check it is a field id, not a farm, crop or event id. A 401 or 403 means this admin session cannot read the endpoint."
          />
        )}

        {!fieldId && !error && (
          <Typography variant="paragraph-m" color="grey-600">
            Paste a field uuid to load its history. Farm-scope and footprint rows that concern the field are included and tagged, because they are frequently
            the cause of its field-scope rows.
          </Typography>
        )}

        <SkeletonWrapper isLoading={isFetching && !data} type="block">
          {!!data && (
            <>
              <ChangeEventHeader data={data} onTypeClick={(type) => patchView({ q: type })} />

              <ChangeEventFilters counts={counts} view={view} canReset={!isDefaultView(view)} onChange={patchView} onReset={resetView} />

              <div className="ce-summary" role="status" aria-live="polite">
                {`${matched.length} of ${data.eventCount} events`}
                {hiddenCount ? ` · ${hiddenCount} ${hiddenScopes.join("/")}-scope hidden` : ""}
              </div>

              {!data.events.length && (
                <div className="ce-empty">
                  No change_events rows for this field. The backfill may not have covered it, or its history predates the event store.
                </div>
              )}

              {!!data.events.length && !matched.length && (
                <div className="ce-empty">
                  {`${data.eventCount} events loaded, none match the current filters. `}
                  <button type="button" className="ce-linkish" onClick={resetView}>
                    reset filters
                  </button>
                </div>
              )}

              <div className="ce-timeline">
                {ordered.map((event, index) => (
                  <React.Fragment key={event.id}>
                    {dayOf(event.createdAt) !== (index ? dayOf(ordered[index - 1].createdAt) : "") && <div className="ce-day">{dayOf(event.createdAt)}</div>}
                    <ChangeEventCard event={event} query={view.q} onJump={jumpTo} />
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
        </SkeletonWrapper>
      </div>
    </MainPage>
  );
};
